import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, LinearProgress, Stack, TextField,
    ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserContext } from "../../theme/userContext";
import DossierView from "./dossierView";
import { ChartLegend, seriesColors, TimeChart } from "./opportunityCharts";
import { DASH, fmtCount, fmtDate, fmtLarge, fmtMultiple, fmtNumber, fmtPct, fmtPctSigned, fmtPrice, germanizeText, hitFlags, isFlagReason, RATING_LABELS,
    toNumber, trackPoints, valuationPoints, YIELD_MEASURES } from "./opportunityFormat";
import { fetchJson, postJson } from "./opportunityApi";

function Metric({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.25 }}>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        </Stack>
    );
}

function SectionTitle({ children }) {
    return <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>{children}</Typography>;
}

function ReasonList({ items }) {
    return (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {items.map((reason, index) => (
                <Typography component="li" variant="body2" key={reason.code || index}>{germanizeText(reason.text)}</Typography>
            ))}
        </Box>
    );
}

function ValuationChart({ hit, history }) {
    const theme = useTheme();
    const palette = seriesColors(theme.palette.mode);
    const measure = YIELD_MEASURES[hit?.yield_measure];
    const points = valuationPoints(history, hit?.yield_measure);
    const median = toNumber(hit?.own_median);
    const currentValue = toNumber(hit?.yield_value);
    const currentDay = String(hit?.entry_price_date || '').slice(0, 10);

    if (points.length < 2) {
        return <Typography variant="body2" sx={{ opacity: 0.75 }}>Für einen Verlauf gibt es zu wenige Stichtage.</Typography>;
    }
    const series = [{ id: 'Rendite', color: palette.primary, data: points, pointRadius: 2 }];
    if (currentValue !== null && currentDay && currentDay > points[points.length - 1].x) {
        series.push({ id: 'Aktuell', color: palette.secondary, data: [{ x: currentDay, y: currentValue, isCurrent: true }], pointRadius: 5, showLine: false });
    }
    return (
        <>
            <ChartLegend items={[
                { kind: 'line', color: palette.primary, label: `${measure?.long || 'Rendite'} je Quartalsende` },
                ...(series.length > 1 ? [{ kind: 'dot', color: palette.secondary, label: 'Aktueller Wert des Laufs' }] : []),
                ...(median !== null ? [{ kind: 'dash', color: theme.palette.text.secondary, label: `Eigener Median ${fmtPct(median)}` }] : []),
            ]} />
            <TimeChart
                series={series}
                height={240}
                yFormat={(value) => fmtPct(value, Math.abs(value) < 0.1 ? 1 : 0)}
                markers={median !== null ? [{ value: median, label: '' }] : []}
                renderTooltip={(point) => (
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {fmtPct(point.data.y)}{point.data.isCurrent ? ' (aktuell)' : ''}
                    </Typography>
                )}
                ariaLabel={`Bewertungsverlauf gegen den eigenen Median ${fmtPct(median)}`}
            />
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.5 }}>
                Der eigene Median stammt aus {fmtCount(hit?.own_median_count)} Quartalen im Fenster vor dem Stichtag. Der Verlauf kann weiter zurückreichen.
            </Typography>
        </>
    );
}

function TrackChart({ track, currency, emptyText }) {
    const theme = useTheme();
    const palette = seriesColors(theme.palette.mode);
    const points = trackPoints(track);
    const note = typeof track?.note === 'string' ? track.note : null;
    if (!track) return <Typography variant="body2" sx={{ opacity: 0.75 }}>{emptyText}</Typography>;

    return (
        <>
            <Typography variant="body2" sx={{ mb: 1 }}>
                Seit {fmtDate(track.from)} ({fmtPrice(track.base_price, currency)}): <b>{fmtPctSigned(track.last_change)}</b>.
                {' '}Letzter Kurs vom {fmtDate(track.last_date)}
                {toNumber(track.age_days) !== null ? `, vor ${fmtCount(track.age_days)} Tagen` : ''}.
                {Array.isArray(track.splits_applied) && track.splits_applied.length > 0 && ` ${track.splits_applied.length} Split(s) berücksichtigt.`}
            </Typography>
            {points.length > 1 ? (
                <TimeChart
                    series={[{ id: 'Kurs', color: palette.primary, data: points, pointRadius: points.length <= 24 ? 3 : 0 }]}
                    height={200}
                    yFormat={(value) => fmtPctSigned(value, 0)}
                    markers={[{ value: 0, label: '' }]}
                    renderTooltip={(point) => (
                        <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{fmtPctSigned(point.data.y)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>{fmtPrice(point.data.close, currency)}</Typography>
                        </>
                    )}
                    ariaLabel="Kursverlauf in Prozent seit dem Ausgangstag"
                />
            ) : (
                <Typography variant="body2" sx={{ opacity: 0.75 }}>Bisher liegt nur ein Kurspunkt vor, deshalb noch kein Verlauf.</Typography>
            )}
            {note && <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.5 }}>{note}</Typography>}
        </>
    );
}

function RatingPanel({ identifier, runId, ratings, ratingTrack, currency, onSaved }) {
    const { user } = useContext(UserContext);
    const current = ratings[0]?.rating || null;
    const [rating, setRating] = useState(current);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => { setRating(current); }, [current]);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await postJson('/api/opportunities/rating', {
                puser: user?.p_user,
                identifier,
                rating,
                note: note.trim() || null,
                run_id: toNumber(runId),
            });
            setNote('');
            setSavedAt(Date.now());
            await onSaved();
        } catch (saveError) {
            setError(saveError.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <ToggleButtonGroup
                exclusive
                size="small"
                color="secondary"
                value={rating}
                onChange={(event, value) => { if (value) setRating(value); }}
                aria-label="Eigene Bewertung"
                sx={{ flexWrap: 'wrap', mb: 1 }}
            >
                {Object.entries(RATING_LABELS).map(([value, label]) => (
                    <ToggleButton key={value} value={value} sx={{ textTransform: 'none' }}>{label}</ToggleButton>
                ))}
            </ToggleButtonGroup>
            <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Notiz (optional)"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                sx={{ mb: 1 }}
            />
            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                <Button variant="contained" color="secondary" disabled={!rating || saving} onClick={save}>
                    {saving ? 'Speichert …' : 'Bewertung speichern'}
                </Button>
                {savedAt && !error && <Typography variant="body2">Gespeichert.</Typography>}
            </Stack>
            {error && <Alert severity="error" sx={{ mt: 1 }}>Speichern fehlgeschlagen: {error}</Alert>}

            {ratingTrack && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>Kurs seit der letzten Bewertung</Typography>
                    <TrackChart track={ratingTrack} currency={currency} emptyText="Kein Kursverlauf verfügbar." />
                </Box>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 0.5 }}>Bisherige Bewertungen</Typography>
            {ratings.length === 0 && <Typography variant="body2" sx={{ opacity: 0.75 }}>Noch keine Bewertung.</Typography>}
            {ratings.map((entry) => (
                <Box key={entry.id} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                        <Chip size="small" label={RATING_LABELS[entry.rating] || DASH} />
                        <Typography variant="caption">{fmtDate(entry.rated_at)}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.75 }}>
                            Kurs {fmtPrice(entry.price_at_mark, currency)} vom {fmtDate(entry.price_date)}
                        </Typography>
                    </Stack>
                    {entry.note && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.note}</Typography>}
                </Box>
            ))}
        </Box>
    );
}

const DOSSIER_POLL_MS = 10000;

/** Nur für Admins: Ein Lauf kostet Modellzeit und EOD-Credits. */
function DossierAction({ identifier, hasDossier, job, onStarted }) {
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);
    const running = job?.status === 'running';

    const start = async () => {
        setStarting(true);
        setError(null);
        try {
            const result = await postJson('/api/opportunities/dossier', { identifier });
            onStarted(result.job);
        } catch (startError) {
            setError(startError.message);
        } finally {
            setStarting(false);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            {running ? (
                <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Dossier wird erstellt. Das dauert meist einige Minuten, die Ansicht aktualisiert sich selbst.</Typography>
                    <LinearProgress color="secondary" />
                </>
            ) : (
                <Button variant="outlined" color="secondary" size="small" disabled={starting} onClick={start}>
                    {hasDossier ? 'Dossier neu erstellen' : 'Dossier erstellen'}
                </Button>
            )}
            {job?.status === 'failed' && !running && (
                <Alert severity="warning" sx={{ mt: 1 }}>Das Dossier konnte nicht erstellt werden: {germanizeText(job.error || 'unbekannter Fehler')}</Alert>
            )}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </Box>
    );
}

const OpportunityDetail = ({ identifier, onClose, onRated, disclaimer, dossierBudget }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { user } = useContext(UserContext);
    const pUser = user?.p_user;
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!identifier || !pUser) return undefined;
        let cancelled = false;
        if (reloadKey === 0) {
            setDetail(null);
        }
        setError(null);
        fetchJson(`/api/opportunities/detail?puser=${encodeURIComponent(pUser)}&identifier=${encodeURIComponent(identifier)}`)
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch((loadError) => { if (!cancelled) setError(loadError.message); });
        return () => { cancelled = true; };
    }, [identifier, pUser, reloadKey]);

    useEffect(() => { setReloadKey(0); }, [identifier]);

    const jobRunning = detail?.dossier_job?.status === 'running';
    useEffect(() => {
        if (!jobRunning || !identifier || !pUser) return undefined;
        let cancelled = false;
        const timer = setInterval(() => {
            fetchJson(`/api/opportunities/detail?puser=${encodeURIComponent(pUser)}&identifier=${encodeURIComponent(identifier)}`)
                .then((data) => {
                    if (cancelled) return;
                    setDetail(data);
                    if (data.dossier_job?.status !== 'running') onRated();
                })
                .catch(() => {});
        }, DOSSIER_POLL_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, [jobRunning, identifier, pUser, onRated]);

    const hit = detail?.hit || null;
    const reasons = (Array.isArray(hit?.reasons) ? hit.reasons : []).filter((reason) => !isFlagReason(reason));
    const flags = hitFlags(hit);
    const dataReasons = Array.isArray(hit?.data_check_reasons) ? hit.data_check_reasons : [];
    const ratings = Array.isArray(detail?.ratings) ? detail.ratings : [];
    const isDataCheck = hit?.list_group === 'data_check';
    const currency = hit?.price_currency;
    const measure = YIELD_MEASURES[hit?.yield_measure];
    const hitAppearances = (Array.isArray(detail?.appearances) ? detail.appearances : []).filter((row) => row.list_group === 'hit');

    const handleSaved = async () => {
        setReloadKey((key) => key + 1);
        await onRated();
    };

    return (
        <Dialog open={Boolean(identifier)} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} scroll="paper">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" component="span" sx={{ fontWeight: 'bold', display: 'block', overflowWrap: 'anywhere' }}>
                        {hit?.name || identifier}
                    </Typography>
                    <Typography variant="body2" component="span" sx={{ opacity: 0.75 }}>
                        {[identifier, hit?.sector, hit?.industry].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label="Schließen"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mb: 1.5 }}>
                    {detail?.disclaimer || disclaimer}
                </Typography>
                {error && <Alert severity="error">Detailansicht konnte nicht geladen werden: {error}</Alert>}
                {!detail && !error && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress color="secondary" /></Box>}
                {detail && (
                    <>
                        {!hit && (
                            <Alert severity="info" sx={{ mb: 2 }}>Dieses Papier steht in keinem Lauf auf der Liste. Es sind nur die eigenen Bewertungen verfügbar.</Alert>
                        )}
                        {isDataCheck && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Daten prüfen: zählt nicht als Treffer</Typography>
                                <ReasonList items={dataReasons} />
                            </Alert>
                        )}

                        {hit && (
                            <>
                                {reasons.length > 0 && (
                                    <>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>Gründe</Typography>
                                        <ReasonList items={reasons} />
                                    </>
                                )}
                                {flags.map((flag) => (
                                    <Alert severity="warning" sx={{ mt: 1 }} key={flag.code}>
                                        <b>{flag.label.charAt(0).toUpperCase() + flag.label.slice(1)}:</b> {flag.text}
                                    </Alert>
                                ))}

                                <SectionTitle>Kennzahlen</SectionTitle>
                                <Grid container columnSpacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <Metric label={measure?.long || 'Rendite'} value={fmtPct(hit.yield_value)} />
                                        {toNumber(hit.yield_raw) !== null && toNumber(hit.yield_raw) !== toNumber(hit.yield_value) && (
                                            <Metric label="Rendite ohne Abzug der Aktienvergütung" value={fmtPct(hit.yield_raw)} />
                                        )}
                                        <Metric label={`Eigener Median (${fmtCount(hit.own_median_count)} Quartale)`} value={fmtPct(hit.own_median)} />
                                        <Metric label="Abstand zum eigenen Median" value={fmtMultiple(hit.distance_to_history)} />
                                        <Metric label="Kurs 12 Monate (um Splits bereinigt)" value={fmtPctSigned(hit.price_change_12m)} />
                                        <Metric label="12 Monate mit Dividenden" value={fmtPctSigned(hit.total_return_12m)} />
                                        <Metric label="Umsatz gegenüber Vorjahr" value={fmtPctSigned(hit.revenue_yoy)} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Metric label="Börsenwert" value={fmtLarge(hit.market_cap, currency)} />
                                        <Metric label="Handelsvolumen je Tag (3 Monate)" value={fmtLarge(hit.avg_dollar_volume_3m, 'USD')} />
                                        <Metric label="Free Cashflow, 12 Monate" value={fmtLarge(hit.fcf_ttm, hit.report_currency)} />
                                        <Metric label="Aktienvergütung, 12 Monate" value={fmtLarge(hit.sbc_ttm, hit.report_currency)} />
                                        <Metric label="Altman Z" value={fmtNumber(hit.altman_z, 1)} />
                                        <Metric label="Veränderung der Aktienzahl" value={fmtPctSigned(hit.dilution_yoy)} />
                                        <Metric label="Kurs bei Aufnahme" value={`${fmtPrice(hit.entry_price, currency)} (${fmtDate(hit.entry_price_date)})`} />
                                        <Metric label="Letzter Berichtsstand" value={fmtDate(hit.latest_period_end)} />
                                    </Grid>
                                </Grid>
                                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.5 }}>
                                    Auf der Liste seit {fmtDate(hit.first_as_of ?? hitAppearances[0]?.run_as_of)}, {fmtCount(hit.runs_on_list ?? hitAppearances.length)} Läufe.
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Button size="small" variant="outlined" color="inherit" component={Link} to="/detailview" state={{ identifier }}>
                                        Zur Wertpapier-Ansicht
                                    </Button>
                                </Box>

                                <SectionTitle>Bewertungsverlauf gegen eigenen Median</SectionTitle>
                                <ValuationChart hit={hit} history={detail.valuation_history} />

                                <SectionTitle>Kursverlauf seit Aufnahme</SectionTitle>
                                <TrackChart track={detail.track} currency={currency} emptyText="Noch kein Kursverlauf verfügbar." />

                                <SectionTitle>Dossier</SectionTitle>
                                {user?.role === 'admin' && !isDataCheck && hit && (
                                    <DossierAction
                                        identifier={identifier}
                                        hasDossier={Boolean(detail.dossier)}
                                        job={detail.dossier_job}
                                        onStarted={(job) => setDetail((current) => ({ ...current, dossier_job: job }))}
                                    />
                                )}
                                <DossierView
                                    dossier={detail.dossier}
                                    state={hit.dossier_state}
                                    budget={dossierBudget}
                                    isDataCheck={isDataCheck}
                                />
                            </>
                        )}

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>Eigene Bewertung</Typography>
                        <RatingPanel
                            key={identifier}
                            identifier={identifier}
                            runId={hit?.run_id}
                            ratings={ratings}
                            ratingTrack={detail.rating_track}
                            currency={currency}
                            onSaved={handleSaved}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default OpportunityDetail;
